import {
  parseAttributes,
} from './document-parser';
import type {
  ImportedAsset,
  ImportedPageDocument,
  WebsiteImportResult,
} from './import-types';
import { resolveImportReference } from './path-resolver';

export type WebsiteGraphPage = {
  id: string;
  sourcePath: string;
  route: string;
  title: string;
  incomingLinks: number;
  outgoingLinks: number;
  assetReferences: number;
  isOrphan: boolean;
};

export type WebsiteGraphAsset = {
  id: string;
  path: string;
  mimeType: string;
  size: number;
  referencedByPages: string[];
  referenceCount: number;
  isShared: boolean;
  isUnused: boolean;
};

export type WebsiteGraphLink = {
  id: string;
  sourcePageId: string;
  targetPageId: string;
  href: string;
  text: string;
};

export type WebsiteGraphAssetReference = {
  id: string;
  pageId: string;
  assetId: string;
  originalReference: string;
  resolvedPath: string;
};

export type WebsiteRelationshipGraph = {
  pages: WebsiteGraphPage[];
  assets: WebsiteGraphAsset[];
  links: WebsiteGraphLink[];
  assetReferences: WebsiteGraphAssetReference[];
};

type AnchorReference = {
  href: string;
  text: string;
};

function createPageId(page: ImportedPageDocument): string {
  return `page:${page.route}`;
}

function createAssetId(asset: ImportedAsset): string {
  return `asset:${asset.normalizedPath}`;
}

function stripHtml(value: string): string {
  return value
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function extractAnchors(
  sourceDocument: string,
): AnchorReference[] {
  return Array.from(
    sourceDocument.matchAll(
      /<a\b([^>]*)>([\s\S]*?)<\/a>/gi,
    ),
  )
    .map((match) => {
      const attributes = parseAttributes(match[1] ?? '');

      return {
        href: attributes.href?.trim() ?? '',
        text: stripHtml(match[2] ?? ''),
      };
    })
    .filter((anchor) => Boolean(anchor.href));
}

function isIgnoredLink(href: string): boolean {
  return /^(?:#|mailto:|tel:|javascript:|data:)/i.test(
    href.trim(),
  );
}

function normalizeRoutePath(pathname: string): string {
  const decodedPath = decodeURIComponent(pathname)
    .replace(/\\/g, '/')
    .replace(/\/{2,}/g, '/');

  if (
    decodedPath === '' ||
    decodedPath === '/' ||
    /\/index\.html?$/i.test(decodedPath)
  ) {
    const withoutIndex = decodedPath.replace(
      /\/index\.html?$/i,
      '/',
    );

    return withoutIndex === '' ? '/' : withoutIndex;
  }

  if (/\.html?$/i.test(decodedPath)) {
    const withoutExtension = decodedPath.replace(
      /\.html?$/i,
      '',
    );

    return `${withoutExtension.replace(/\/$/, '')}/`;
  }

  return decodedPath.endsWith('/')
    ? decodedPath
    : `${decodedPath}/`;
}

function resolveLinkedRoute(
  sourcePage: ImportedPageDocument,
  href: string,
): string | null {
  if (isIgnoredLink(href)) {
    return null;
  }

  try {
    const baseUrl = new URL(
      sourcePage.route,
      'https://panderr-import.local',
    );

    const targetUrl = new URL(href, baseUrl);

    if (
      targetUrl.origin !==
      'https://panderr-import.local'
    ) {
      return null;
    }

    return normalizeRoutePath(targetUrl.pathname);
  } catch {
    return null;
  }
}

function buildLinks(
  pages: ImportedPageDocument[],
): WebsiteGraphLink[] {
  const pageByRoute = new Map(
    pages.map((page) => [page.route, page]),
  );

  const links: WebsiteGraphLink[] = [];
  const seen = new Set<string>();

  for (const sourcePage of pages) {
    const sourcePageId = createPageId(sourcePage);

    for (const anchor of extractAnchors(
      sourcePage.sourceDocument,
    )) {
      const targetRoute = resolveLinkedRoute(
        sourcePage,
        anchor.href,
      );

      if (!targetRoute) {
        continue;
      }

      const targetPage = pageByRoute.get(targetRoute);

      if (!targetPage) {
        continue;
      }

      const targetPageId = createPageId(targetPage);
      const identity = [
        sourcePageId,
        targetPageId,
        anchor.href,
        anchor.text,
      ].join('|');

      if (seen.has(identity)) {
        continue;
      }

      seen.add(identity);

      links.push({
        id: `link:${links.length + 1}`,
        sourcePageId,
        targetPageId,
        href: anchor.href,
        text: anchor.text,
      });
    }
  }

  return links;
}

function buildAssetReferences(
  result: WebsiteImportResult,
): WebsiteGraphAssetReference[] {
  const assetByPath = new Map(
    result.assets.map((asset) => [
      asset.normalizedPath,
      asset,
    ]),
  );

  const references: WebsiteGraphAssetReference[] = [];
  const seen = new Set<string>();

  for (const page of result.pages) {
    const pageId = createPageId(page);

    for (const originalReference of page.assetPaths) {
      const resolved = resolveImportReference(
        page.sourcePath,
        originalReference,
      );

      if (resolved.kind !== 'local') {
        continue;
      }

      const asset = assetByPath.get(resolved.resolvedPath);

      if (!asset) {
        continue;
      }

      const assetId = createAssetId(asset);
      const identity = [
        pageId,
        assetId,
        originalReference,
      ].join('|');

      if (seen.has(identity)) {
        continue;
      }

      seen.add(identity);

      references.push({
        id: `asset-reference:${references.length + 1}`,
        pageId,
        assetId,
        originalReference,
        resolvedPath: resolved.resolvedPath,
      });
    }
  }

  return references;
}

export function buildWebsiteRelationshipGraph(
  result: WebsiteImportResult,
): WebsiteRelationshipGraph {
  const links = buildLinks(result.pages);
  const assetReferences = buildAssetReferences(result);

  const pages = result.pages.map((page) => {
    const id = createPageId(page);

    const incomingLinks = links.filter(
      (link) => link.targetPageId === id,
    ).length;

    const outgoingLinks = links.filter(
      (link) => link.sourcePageId === id,
    ).length;

    const referenceCount = assetReferences.filter(
      (reference) => reference.pageId === id,
    ).length;

    return {
      id,
      sourcePath: page.sourcePath,
      route: page.route,
      title: page.title,
      incomingLinks,
      outgoingLinks,
      assetReferences: referenceCount,
      isOrphan:
        page.route !== '/' && incomingLinks === 0,
    };
  });

  const assets = result.assets.map((asset) => {
    const id = createAssetId(asset);

    const references = assetReferences.filter(
      (reference) => reference.assetId === id,
    );

    const referencedByPages = Array.from(
      new Set(
        references.map((reference) => reference.pageId),
      ),
    ).sort();

    return {
      id,
      path: asset.normalizedPath,
      mimeType: asset.mimeType,
      size: asset.size,
      referencedByPages,
      referenceCount: references.length,
      isShared: referencedByPages.length > 1,
      isUnused: references.length === 0,
    };
  });

  return {
    pages,
    assets,
    links,
    assetReferences,
  };
}
